import React, { useCallback, useEffect, useState } from 'react';
import theme from './OptionSigningPre.module.scss';
import { useHistory } from 'react-router';
import { Accordion, Button, Card, Badge } from '@ygtechnologies/rubus-ui';
import { Banner, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { useGetActiveProfile } from '@/utils/hooks';
import { configuratorKeys, pickDefaults, prepareConfiguratorSignature } from '@/services/configurator';
import { BuyerOptionCard } from '../BuyerOptionCard';
import { ProductCardList } from '@/seller/components/products/ProductCard';
import { buildConfiguratorUrl } from '../utils/buildConfiguratorUrl';
import { getRoomDisplayName } from '../utils/configuratorRooms';

export const OptionSigningPre: React.FC = () => {
    const {
        configuratorData,
        rooms,
        selectedDeadlineId,
        residentialUnitId,
        baseUrl,
        setTabsEnabled,
        setBottomPanelEnabled,
    } = useOptionConfigurator();
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const profile = useGetActiveProfile();
    const queryClient = useQueryClient();

    useEffect(() => {
        setTabsEnabled(false);
        setBottomPanelEnabled(false);
        return () => {
            setTabsEnabled(true);
            setBottomPanelEnabled(true);
        };
    }, []);

    const roomsWithUnselected = rooms
        .map((room) => ({
            roomId: room.roomId,
            roomName: getRoomDisplayName(room),
            unselectedGroups: room.groups.filter(
                (g) => g.applicable && g.selectedOption === null && !g.options.find((o) => o.selected)
            ),
        }))
        .filter((room) => room.unselectedGroups.length > 0);

    const totalGroups = rooms.reduce((acc, room) => acc + room.groups.filter((g) => g.applicable).length, 0);

    const totalUnselected = roomsWithUnselected.reduce((acc, room) => acc + room.unselectedGroups.length, 0);

    const proceed = useCallback(async () => {
        if (!selectedDeadlineId || !profile) return;
        try {
            setLoading(true);
            const signResult = await prepareConfiguratorSignature(residentialUnitId, selectedDeadlineId, profile.id);
            history.replace(
                buildConfiguratorUrl({
                    baseUrl,
                    deadlineId: selectedDeadlineId,
                    segment: `sign/${signResult.clientKey}`,
                })
            );
        } catch {
            Toast.error(t`Something went wrong. Please try again.`);
            setLoading(false);
        }
    }, [baseUrl, history, profile, residentialUnitId, selectedDeadlineId]);

    useEffect(() => {
        if (!configuratorData || !selectedDeadlineId || !profile) return;
        if (totalUnselected === 0) {
            proceed();
        }
    }, [configuratorData, selectedDeadlineId, profile, totalUnselected, proceed]);

    const handleApplyDefaults = async () => {
        if (!selectedDeadlineId || !profile) return;
        try {
            setLoading(true);
            const updatedData = await pickDefaults(residentialUnitId, selectedDeadlineId);

            const stillUnselected = updatedData.groups.filter((g) => g.applicable && g.selectedOption === null);

            queryClient.invalidateQueries({ queryKey: configuratorKeys.byUnit(residentialUnitId) });

            if (stillUnselected.length > 0) {
                Toast.error(
                    t`${stillUnselected.length} option(s) have no default and must be selected manually before signing.`
                );
                setLoading(false);
                return;
            }

            const signResult = await prepareConfiguratorSignature(residentialUnitId, selectedDeadlineId, profile.id);
            history.replace(
                buildConfiguratorUrl({
                    baseUrl,
                    deadlineId: selectedDeadlineId,
                    segment: `sign/${signResult.clientKey}`,
                })
            );
        } catch {
            Toast.error(t`Something went wrong. Please try again.`);
            setLoading(false);
        }
    };

    if (!configuratorData || loading || totalUnselected === 0) {
        return null;
    }

    return (
        <div className={theme.container}>
            <Banner type="warning">
                <strong>
                    <Trans>
                        {totalUnselected} out of {totalGroups} options do not have a selection.
                    </Trans>
                </strong>
                <p>
                    <Trans>
                        To continue signing, you need to make a selection for all options. You can apply the default
                        selection for all remaining options using the button below.
                    </Trans>
                </p>
            </Banner>

            {roomsWithUnselected.map((room) => (
                <Accordion
                    key={room.roomId}
                    isOpen={true}
                    title={
                        <div className={theme.accordionTitle}>
                            <span>{room.roomName}</span>
                            <Badge appearance="subtle">
                                <Trans>{room.unselectedGroups.length} missing</Trans>
                            </Badge>
                        </div>
                    }
                    className={theme.accordion}>
                    <ProductCardList>
                        {room.unselectedGroups.map((g) => {
                            const defaultOption = g.options.find((o) => o.isDefault);
                            if (!defaultOption) {
                                return (
                                    <Card key={g.id} className={theme.noDefaultCard}>
                                        <span className={theme.groupName}>{g.name}</span>
                                        <span className={theme.noDefault}>
                                            <Trans>No default — select manually</Trans>
                                        </span>
                                    </Card>
                                );
                            }
                            return (
                                <BuyerOptionCard key={g.id} option={defaultOption} groupName={g.name} hideSelectionSlot />
                            );
                        })}
                    </ProductCardList>
                </Accordion>
            ))}

            <div className={theme.buttons}>
                <Button variant="tertiary" onClick={() => history.push(baseUrl)}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button onClick={handleApplyDefaults} loading={loading}>
                    <Trans>Select default options for all</Trans>
                </Button>
            </div>
        </div>
    );
};
